import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  SkipNext as SkipNextIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  SportsTennis as SportsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useSearchParams } from 'react-router-dom';
import VideoPlayer from '@components/VideoPlayer';

interface VideoEvaluationQueueProps {
  sport?: string; // Sport passed from parent (AdminDashboard)
}

const AdminVideoEvaluationQueue: React.FC<VideoEvaluationQueueProps> = ({ sport: propSport }) => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  // Get sport from prop, URL, or default to Badminton
  const sportFromUrl = searchParams.get('sport') || propSport || 'Badminton';
  const [selectedSport, setSelectedSport] = useState<string>(sportFromUrl);
  const [currentVideo, setCurrentVideo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [queueStats, setQueueStats] = useState<any>({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [pendingVideos, setPendingVideos] = useState<any[]>([]);
  const [evaluatedVideos, setEvaluatedVideos] = useState<any[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'evaluated'>('pending');
  
  // Filter states
  const [filterAgeCategory, setFilterAgeCategory] = useState<string>('');
  const [filterCountry, setFilterCountry] = useState<string>('');
  const [filterExamType, setFilterExamType] = useState<string>('');
  const [filterStrokeCode, setFilterStrokeCode] = useState<string>('');
  const [filterVerificationStatus, setFilterVerificationStatus] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('fifo');
  
  // Filter options (populated from video data)
  const [filterOptions, setFilterOptions] = useState<{
    ageCategories: string[];
    countries: string[];
    examTypes: string[];
    strokeCodes: string[];
  }>({
    ageCategories: [],
    countries: [],
    examTypes: [],
    strokeCodes: []
  });
  const [evaluationStartTime, setEvaluationStartTime] = useState<Date | null>(null);
  const [lockExpiresAt, setLockExpiresAt] = useState<Date | null>(null);
  const lockExtendIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Evaluation form state
  const [verificationStatus, setVerificationStatus] = useState<'approved' | 'rejected'>('approved');
  const [rating, setRating] = useState<number>(0);
  const [ratingNotes, setRatingNotes] = useState('');
  const [adminFeedback, setAdminFeedback] = useState('');

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  // Load queue statistics - sport-specific, grouped by exam type
  const loadQueueStats = async () => {
    if (!user?.id || !selectedSport) {
      console.log('[Queue Stats] No user ID or sport, skipping load');
      return;
    }
    
    setStatsLoading(true);
    try {
      console.log('[Queue Stats] Loading stats for user:', user.id, 'sport:', selectedSport);
      // Load pending videos to group by exam type
      const videosResponse = await fetch(
        `/api/admin/videos/queue/list?userId=${user?.id}&sport=${selectedSport}`,
        { headers: getAuthHeaders() }
      );
      
      if (!videosResponse.ok) {
        throw new Error(`HTTP ${videosResponse.status}: ${videosResponse.statusText}`);
      }
      
      const videosData = await videosResponse.json();
      console.log('[Queue Stats] Videos response:', videosData);
      
      // Group stats by exam type (stroke-shot, drill, fitness)
      const statsByExamType: any = {};
      if (videosData.success && videosData.data && Array.isArray(videosData.data)) {
        videosData.data.forEach((video: any) => {
          const examType = video.exam_type || 'stroke-shot';
          if (!statsByExamType[examType]) {
            statsByExamType[examType] = {
              pending: 0,
              locked: 0,
              evaluatedToday: 0
            };
          }
          if (video.verification_status === 'pending') {
            statsByExamType[examType].pending++;
          }
          if (video.is_locked) {
            statsByExamType[examType].locked++;
          }
        });
      }
      
      // Get overall stats from stats endpoint
      const statsResponse = await fetch(
        `/api/admin/videos/queue/stats?userId=${user?.id}&sport=${selectedSport}`,
        { headers: getAuthHeaders() }
      );
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.success && statsData.data) {
          statsByExamType._overall = {
            pending: statsData.data.pending || 0,
            locked: statsData.data.locked || 0,
            evaluatedToday: statsData.data.evaluatedToday || 0
          };
        }
      }
      
      console.log('[Queue Stats] Setting stats by exam type:', statsByExamType);
      setQueueStats(statsByExamType);
      setError(null);
    } catch (err: any) {
      console.error('[Queue Stats] Error:', err);
      setError(err.message || 'Failed to load queue statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  // Get next video from queue
  const getNextVideo = async () => {
    if (!selectedSport) {
      setError('Sport is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(
        `/api/admin/videos/queue/next?userId=${user?.id}&sport=${selectedSport}`,
        { headers: getAuthHeaders() }
      );
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to get next video');
      }

      if (!data.data) {
        setError('No videos available in queue');
        setCurrentVideo(null);
        return;
      }

      setCurrentVideo(data.data);
      setEvaluationStartTime(new Date());
      setLockExpiresAt(new Date(data.data.lockExpiresAt));
      
      // Reset form
      setVerificationStatus('approved');
      setRating(0);
      setRatingNotes('');
      setAdminFeedback('');

      // Start lock extension interval (extend every 25 minutes)
      if (lockExtendIntervalRef.current) {
        clearInterval(lockExtendIntervalRef.current);
      }
      lockExtendIntervalRef.current = setInterval(() => {
        extendLock();
      }, 25 * 60 * 1000); // 25 minutes

      await loadQueueStats();
      await loadPendingVideos();
    } catch (err: any) {
      console.error('Error getting next video:', err);
      setError(err.message || 'Failed to get next video');
      setCurrentVideo(null);
    } finally {
      setLoading(false);
    }
  };

  // Load pending videos list
  const loadPendingVideos = async () => {
    if (!user?.id || !selectedSport) {
      console.log('[Pending Videos] No user ID or sport, skipping load');
      return;
    }
    
    setVideosLoading(true);
    try {
      const params = new URLSearchParams({
        userId: user.id,
        sport: selectedSport,
        ...(filterAgeCategory && { ageCategory: filterAgeCategory }),
        ...(filterCountry && { country: filterCountry }),
        ...(filterExamType && { examType: filterExamType }),
        ...(filterStrokeCode && { strokeCode: filterStrokeCode }),
        ...(sortBy && sortBy !== 'fifo' && { sortBy })
      });
      
      console.log('[Pending Videos] Loading videos with params:', params.toString());
      const response = await fetch(
        `/api/admin/videos/queue/list?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[Pending Videos] Response:', data);
      console.log('[Pending Videos] Data array:', data.data);
      console.log('[Pending Videos] Data length:', data.data?.length);
      
      if (data.success) {
        const videos = Array.isArray(data.data) ? data.data : [];
        console.log('[Pending Videos] Setting videos:', videos.length);
        setPendingVideos(videos);
        
        // Update filter options from video data
        const ageCategories = [...new Set(videos.map((v: any) => v.age_category || v.ageCategory).filter(Boolean))].sort();
        const countries = [...new Set(videos.map((v: any) => v.country).filter(Boolean))].sort();
        const examTypes = [...new Set(videos.map((v: any) => v.exam_type || v.examTypeLabel).filter(Boolean))].sort();
        const strokeCodes = [...new Set(videos.map((v: any) => v.stroke_code || v.strokeCode).filter(Boolean))].sort();
        setFilterOptions({
          ageCategories,
          countries,
          examTypes,
          strokeCodes
        });
        
        setError(null);
      } else {
        console.error('[Pending Videos] Failed:', data.message);
        setError(data.message || 'Failed to load pending videos');
        setPendingVideos([]);
      }
    } catch (err: any) {
      console.error('[Pending Videos] Error:', err);
      setError(err.message || 'Failed to load pending videos');
    } finally {
      setVideosLoading(false);
    }
  };

  // Load evaluated videos list
  const loadEvaluatedVideos = async () => {
    if (!user?.id || !selectedSport) {
      console.log('[Evaluated Videos] No user ID or sport, skipping load');
      return;
    }
    
    setVideosLoading(true);
    try {
      const params = new URLSearchParams({
        userId: user.id,
        sport: selectedSport,
        ...(filterAgeCategory && { ageCategory: filterAgeCategory }),
        ...(filterCountry && { country: filterCountry }),
        ...(filterExamType && { examType: filterExamType }),
        ...(filterStrokeCode && { strokeCode: filterStrokeCode }),
        ...(filterVerificationStatus && { verificationStatus: filterVerificationStatus }),
        ...(sortBy && { sortBy })
      });
      
      console.log('[Evaluated Videos] Loading videos with params:', params.toString());
      const response = await fetch(
        `/api/admin/videos/queue/evaluated?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[Evaluated Videos] Response:', data);
      console.log('[Evaluated Videos] Data array:', data.data);
      console.log('[Evaluated Videos] Data length:', data.data?.length);
      
      if (data.success) {
        const videos = Array.isArray(data.data) ? data.data : [];
        console.log('[Evaluated Videos] Setting videos:', videos.length);
        setEvaluatedVideos(videos);
        
        // Update filter options from video data
        const ageCategories = [...new Set(videos.map((v: any) => v.age_category || v.ageCategory).filter(Boolean))].sort();
        const countries = [...new Set(videos.map((v: any) => v.country).filter(Boolean))].sort();
        const examTypes = [...new Set(videos.map((v: any) => v.exam_type || v.examTypeLabel).filter(Boolean))].sort();
        const strokeCodes = [...new Set(videos.map((v: any) => v.stroke_code || v.strokeCode).filter(Boolean))].sort();
        setFilterOptions({
          ageCategories,
          countries,
          examTypes,
          strokeCodes
        });
        
        setError(null);
      } else {
        console.error('[Evaluated Videos] Failed:', data.message);
        setError(data.message || 'Failed to load evaluated videos');
        setEvaluatedVideos([]);
      }
    } catch (err: any) {
      console.error('[Evaluated Videos] Error:', err);
      setError(err.message || 'Failed to load evaluated videos');
    } finally {
      setVideosLoading(false);
    }
  };

  // Extend video lock
  const extendLock = async () => {
    if (!currentVideo) return;
    
    try {
      const response = await fetch(
        `/api/admin/videos/${currentVideo.id}/extend-lock?sport=${currentVideo.sport}&minutes=30&userId=${user?.id}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      
      const data = await response.json();
      if (data.success) {
        setLockExpiresAt(new Date(data.data.expiresAt));
      }
    } catch (err: any) {
      console.error('Error extending lock:', err);
    }
  };

  // Release video (skip/return to queue)
  const releaseVideo = async () => {
    if (!currentVideo) return;
    
    try {
      const response = await fetch(
        `/api/admin/videos/${currentVideo.id}/release?sport=${currentVideo.sport}&userId=${user?.id}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      
      const data = await response.json();
      if (data.success) {
        setCurrentVideo(null);
        setEvaluationStartTime(null);
        setLockExpiresAt(null);
        if (lockExtendIntervalRef.current) {
          clearInterval(lockExtendIntervalRef.current);
        }
        await loadQueueStats();
        setSuccess('Video released back to queue');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Error releasing video:', err);
      setError(err.message || 'Failed to release video');
    }
  };

  // Submit evaluation
  const submitEvaluation = async () => {
    if (!currentVideo) return;
    
    // Validation
    if (verificationStatus === 'rejected' && !adminFeedback.trim()) {
      setError('Admin feedback is required when rejecting a video');
      return;
    }
    
    if (verificationStatus === 'rejected' && rating > 0) {
      setError('Rating should be 0 for rejected videos');
      return;
    }
    
    setEvaluating(true);
    setError(null);
    
    try {
      // Calculate evaluation time
      const evaluationTimeSeconds = evaluationStartTime
        ? Math.floor((new Date().getTime() - evaluationStartTime.getTime()) / 1000)
        : 0;
      
      // Set rating to 0 if rejected
      const finalRating = verificationStatus === 'rejected' ? 0 : rating;

      const response = await fetch(
        `/api/admin/videos/${currentVideo.id}/evaluate?userId=${user?.id}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            sport: currentVideo.sport,
            verificationStatus,
            rating: finalRating,
            ratingNotes: verificationStatus === 'rejected' ? '' : ratingNotes,
            adminFeedback,
            evaluationTimeSeconds,
          }),
        }
      );
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to submit evaluation');
      }

      setSuccess('Video evaluated successfully!');
      setCurrentVideo(null);
      setEvaluationStartTime(null);
      setLockExpiresAt(null);
      if (lockExtendIntervalRef.current) {
        clearInterval(lockExtendIntervalRef.current);
      }
      
      await loadQueueStats();
      
      // Reload the appropriate video list
      if (activeTab === 'pending') {
        await loadPendingVideos();
        // Auto-get next video after 2 seconds
        setTimeout(() => {
          getNextVideo();
        }, 2000);
      } else {
        await loadEvaluatedVideos();
      }
    } catch (err: any) {
      console.error('Error submitting evaluation:', err);
      setError(err.message || 'Failed to submit evaluation');
    } finally {
      setEvaluating(false);
    }
  };

  // Update selectedSport when prop or URL changes
  useEffect(() => {
    const sportFromUrl = searchParams.get('sport') || propSport || 'Badminton';
    if (sportFromUrl !== selectedSport) {
      setSelectedSport(sportFromUrl);
      // Update URL if sport changed
      if (sportFromUrl && !searchParams.get('sport')) {
        setSearchParams({ sport: sportFromUrl });
      }
    }
  }, [propSport, searchParams, selectedSport, setSearchParams]);

  // Load stats on mount and when sport changes
  useEffect(() => {
    if (user?.id && selectedSport) {
      loadQueueStats();
      if (activeTab === 'pending') {
        loadPendingVideos();
      } else {
        loadEvaluatedVideos();
      }
      const interval = setInterval(() => {
        loadQueueStats();
        if (activeTab === 'pending') {
          loadPendingVideos();
        } else {
          loadEvaluatedVideos();
        }
      }, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport, user?.id, activeTab]);

  // Reload videos when filters change or tab changes
  useEffect(() => {
    if (user?.id) {
      if (activeTab === 'pending') {
        loadPendingVideos();
      } else {
        loadEvaluatedVideos();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAgeCategory, filterCountry, filterExamType, filterStrokeCode, filterVerificationStatus, sortBy, activeTab]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (lockExtendIntervalRef.current) {
        clearInterval(lockExtendIntervalRef.current);
      }
      // Release video if still locked
      if (currentVideo) {
        releaseVideo().catch(console.error);
      }
    };
  }, []);

  // Calculate time remaining on lock
  const getLockTimeRemaining = () => {
    if (!lockExpiresAt) return null;
    const now = new Date();
    const expires = new Date(lockExpiresAt);
    const diff = expires.getTime() - now.getTime();
    if (diff <= 0) return 'Expired';
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1976d2' }}>
          Video Evaluation Queue - {selectedSport}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Evaluate videos in submission order (FIFO). Videos are automatically locked to prevent conflicts.
        </Typography>

        {/* Queue Statistics - Sport-specific, grouped by exam type */}
        {statsLoading ? (
          <Box sx={{ mb: 3, textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading queue statistics...
            </Typography>
          </Box>
        ) : Object.keys(queueStats).length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            No queue statistics loaded. Click "Refresh Stats" button to load statistics.
          </Alert>
        ) : (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(queueStats)
              .filter(([key]) => key !== '_overall') // Exclude overall stats from cards
              .map(([examType, stats]: [string, any]) => {
                const examTypeLabel = examType === 'stroke-shot' ? 'Strokes & Shots' :
                                     examType === 'drill' ? 'Drills' :
                                     examType === 'fitness' ? 'Fitness' :
                                     examType;
                return (
                  <Grid item xs={12} sm={6} md={4} key={examType}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {examTypeLabel}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                          {stats.pending || 0} Pending
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stats.locked || 0} In Progress • {stats.evaluatedToday || 0} Today
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        )}

        {/* Refresh Stats Button */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadQueueStats}
            size="small"
          >
            Refresh Stats
          </Button>
        </Box>
        
        {/* Filters */}
        {selectedSport && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Age Category</InputLabel>
                    <Select
                      value={filterAgeCategory}
                      onChange={(e) => setFilterAgeCategory(e.target.value)}
                      label="Age Category"
                    >
                      <MenuItem value="">All</MenuItem>
                      {filterOptions.ageCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={filterCountry}
                      onChange={(e) => setFilterCountry(e.target.value)}
                      label="Country"
                    >
                      <MenuItem value="">All</MenuItem>
                      {filterOptions.countries.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Evaluation Category</InputLabel>
                    <Select
                      value={filterExamType}
                      onChange={(e) => setFilterExamType(e.target.value)}
                      label="Evaluation Category"
                    >
                      <MenuItem value="">All</MenuItem>
                      {filterOptions.examTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Stroke Code</InputLabel>
                    <Select
                      value={filterStrokeCode}
                      onChange={(e) => setFilterStrokeCode(e.target.value)}
                      label="Stroke Code"
                    >
                      <MenuItem value="">All</MenuItem>
                      {filterOptions.strokeCodes.map((code) => (
                        <MenuItem key={code} value={code}>{code}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {activeTab === 'evaluated' && (
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterVerificationStatus}
                        onChange={(e) => setFilterVerificationStatus(e.target.value)}
                        label="Status"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="approved">Verified</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={activeTab === 'evaluated' ? 2 : 4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Sort By"
                    >
                      {activeTab === 'pending' ? (
                        <>
                          <MenuItem value="fifo">FIFO (Oldest First)</MenuItem>
                          <MenuItem value="newest">Newest First</MenuItem>
                        </>
                      ) : (
                        <>
                          <MenuItem value="recent">Most Recent</MenuItem>
                          <MenuItem value="oldest">Oldest First</MenuItem>
                          <MenuItem value="rating_high">Rating (High to Low)</MenuItem>
                          <MenuItem value="rating_low">Rating (Low to High)</MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Videos Table - Pending or Evaluated */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {activeTab === 'pending' 
                ? `Pending Videos (${pendingVideos.length})`
                : `Evaluated Videos (${evaluatedVideos.length})`}
            </Typography>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue as 'pending' | 'evaluated')}>
              <Tab label="Pending" value="pending" />
              <Tab label="Evaluated" value="evaluated" />
            </Tabs>
          </Box>
          {videosLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading videos...
              </Typography>
            </Box>
          ) : activeTab === 'pending' ? (
            pendingVideos.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No pending videos found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Player ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Age Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluation Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluation Period</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Stroke Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Attempt</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingVideos.map((video: any) => (
                    <TableRow key={video.id} hover>
                      <TableCell>{video.playerIdCode}</TableCell>
                      <TableCell>{video.ageCategory}</TableCell>
                      <TableCell>{video.country}</TableCell>
                      <TableCell>{video.examTypeLabel}</TableCell>
                      <TableCell>{video.evaluationPeriod}</TableCell>
                      <TableCell>
                        <Chip 
                          label={video.strokeCode} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`Attempt ${video.upload_attempt_number || video.uploadAttemptNumber || 1}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {video.uploadedAt 
                          ? new Date(video.uploadedAt).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {video.isLocked ? (
                          <Chip 
                            icon={<LockIcon />}
                            label={`Locked by ${video.lockedBy || 'Admin'}`}
                            size="small"
                            color="warning"
                          />
                        ) : (
                          <Chip 
                            label="Available"
                            size="small"
                            color="success"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PlayArrowIcon />}
                          onClick={async () => {
                            // Get this specific video for evaluation
                            try {
                              setLoading(true);
                              const response = await fetch(
                                `/api/admin/videos/queue/next?userId=${user?.id}&sport=${video.sport}&videoId=${video.id}`,
                                { headers: getAuthHeaders() }
                              );
                              const data = await response.json();
                              if (data.success && data.data) {
                                setCurrentVideo(data.data);
                                setEvaluationStartTime(new Date());
                                setLockExpiresAt(new Date(data.data.lockExpiresAt));
                                setVerificationStatus('approved');
                                setRating(0);
                                setRatingNotes('');
                                setAdminFeedback('');
                                await loadPendingVideos();
                              } else {
                                setError(data.message || 'Failed to get video');
                              }
                            } catch (err: any) {
                              setError(err.message || 'Failed to get video');
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={video.isLocked && video.lockedBy !== `${user?.firstName} ${user?.lastName}`}
                        >
                          Evaluate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )
          ) : (
            evaluatedVideos.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No evaluated videos found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Player ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Age Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluation Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluation Period</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Stroke Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Attempt</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluated By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluated At</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Evaluation Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluatedVideos.map((video: any) => (
                      <TableRow key={video.id} hover>
                        <TableCell>{video.player_id_code || video.playerIdCode || 'N/A'}</TableCell>
                        <TableCell>{video.age_category || 'N/A'}</TableCell>
                        <TableCell>{video.country || 'N/A'}</TableCell>
                        <TableCell>{video.exam_type || video.examTypeLabel || 'N/A'}</TableCell>
                        <TableCell>{video.evaluation_period || video.evaluationPeriod || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={video.stroke_code || video.strokeCode || 'N/A'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`Attempt ${video.upload_attempt_number || video.uploadAttemptNumber || 1}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={video.verification_status === 'approved' ? 'Verified' : video.verification_status}
                            size="small"
                            color={
                              video.verification_status === 'approved' ? 'success' :
                              video.verification_status === 'rejected' ? 'error' :
                              'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {video.rating !== null && video.rating !== undefined ? (
                            <Rating value={Number(video.rating)} readOnly size="small" />
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {video.verifier_name || video.verifierName || (video.verified_by ? 'Admin User' : 'Not Evaluated')}
                        </TableCell>
                        <TableCell>
                          {video.verified_at 
                            ? new Date(video.verified_at).toLocaleDateString()
                            : video.updated_at
                            ? new Date(video.updated_at).toLocaleDateString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {video.evaluation_time_seconds 
                            ? `${Math.floor(video.evaluation_time_seconds / 60)}m ${video.evaluation_time_seconds % 60}s`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlayArrowIcon />}
                            onClick={async () => {
                              try {
                                setLoading(true);
                                setError(null);
                                
                                // Determine if this is an evaluated video
                                const isEvaluated = video.verification_status && 
                                  ['approved', 'rejected'].includes(video.verification_status);
                                
                                // Get sport - it should be in the video object from getAllEvaluatedVideos
                                const videoSport = video.sport || selectedSport;
                                
                                if (!videoSport) {
                                  setError('Sport information is missing.');
                                  return;
                                }
                                
                                // Use viewOnly=true for evaluated videos (no lock needed)
                                const url = `/api/admin/videos/queue/next?userId=${user?.id}&sport=${videoSport}&videoId=${video.id}${isEvaluated ? '&viewOnly=true' : ''}`;
                                console.log('[View Evaluated Video] Video:', { 
                                  id: video.id, 
                                  sport: videoSport, 
                                  status: video.verification_status, 
                                  isEvaluated,
                                  url 
                                });
                                
                                const response = await fetch(url, { headers: getAuthHeaders() });
                                const data = await response.json();
                                
                                console.log('[View Evaluated Video] Response:', data);
                                
                                if (data.success && data.data) {
                                  setCurrentVideo(data.data);
                                  // For evaluated videos, don't set lock times
                                  if (isEvaluated) {
                                    setEvaluationStartTime(null);
                                    setLockExpiresAt(null);
                                  } else {
                                    setEvaluationStartTime(new Date());
                                    setLockExpiresAt(new Date(data.data.lockExpiresAt));
                                  }
                                  setVerificationStatus(video.verification_status || 'approved');
                                  setRating(video.rating ? Number(video.rating) : 0);
                                  setRatingNotes(video.rating_notes || '');
                                  setAdminFeedback(video.admin_feedback || '');
                                } else {
                                  const errorMsg = data.message || 'Failed to load video';
                                  console.error('[View Evaluated Video] Error:', errorMsg);
                                  setError(errorMsg);
                                }
                              } catch (err: any) {
                                console.error('[View Evaluated Video] Exception:', err);
                                setError(err.message || 'Failed to load video');
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Paper>

        {/* Get Next Video Button (for quick FIFO access) */}
        {!currentVideo && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={getNextVideo}
              disabled={loading}
              sx={{ px: 4, py: 1.5 }}
            >
              {loading ? 'Loading...' : 'Get Next Video (FIFO)'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Or select a video from the table above to evaluate
            </Typography>
          </Box>
        )}

        {/* Current Video Evaluation */}
        {currentVideo && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {currentVideo.verification_status && currentVideo.verification_status !== 'pending' ? 'Viewing Evaluated Video' : 'Evaluating Video'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentVideo.playerIdCode || currentVideo.player_id_code || 'N/A'} • {currentVideo.sport} • {currentVideo.stroke_code || 'N/A'}
                    {currentVideo.verifier_name && (
                      <> • Evaluated by: {currentVideo.verifier_name}</>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {lockExpiresAt && (
                    <Chip
                      icon={<TimerIcon />}
                      label={`Lock: ${getLockTimeRemaining()}`}
                      color={getLockTimeRemaining()?.includes('Expired') ? 'error' : 'default'}
                      size="small"
                    />
                  )}
                  {currentVideo.verification_status && currentVideo.verification_status === 'pending' && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LockOpenIcon />}
                      onClick={releaseVideo}
                    >
                      Skip / Release
                    </Button>
                  )}
                </Box>
              </Box>
              
              {/* Info Alert */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Rating Notes:</strong> Internal notes for admin reference only (not visible to player)
                  <br />
                  <strong>Admin Feedback:</strong> Visible to player. Required for "Rejected" status.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                {/* Video Player */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Player Video
                    </Typography>
                    {currentVideo.video_url ? (
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          backgroundColor: '#f5f5f5',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <VideoPlayer url={currentVideo.video_url} />
                      </Paper>
                    ) : (
                      <Alert severity="warning">Video URL not available</Alert>
                    )}
                  </Box>

                  {/* Reference Videos */}
                  {currentVideo.reference_videos && currentVideo.reference_videos.trim().length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Reference Videos (for comparison)
                      </Typography>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          backgroundColor: '#e3f2fd',
                          border: '1px solid #90caf9'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {currentVideo.reference_videos
                            .split(',')
                            .map((url: string) => url.trim())
                            .filter((url: string) => url.length > 0)
                            .map((url: string, idx: number) => (
                              <Box key={idx}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                  Reference {idx + 1}:
                                </Typography>
                                <VideoPlayer url={url} />
                              </Box>
                            ))}
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  {/* Video Details */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Player</TableCell>
                          <TableCell>
                            {currentVideo.player_name || currentVideo.playerIdCode || currentVideo.player_id_code || 'N/A'}
                            {currentVideo.player_name && (currentVideo.playerIdCode || currentVideo.player_id_code) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                ID: {currentVideo.playerIdCode || currentVideo.player_id_code}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Sport</TableCell>
                          <TableCell>{currentVideo.sport}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Stroke Code</TableCell>
                          <TableCell>
                            <Chip label={currentVideo.stroke_code || 'N/A'} size="small" color="primary" />
                          </TableCell>
                        </TableRow>
                        {currentVideo.stroke_type && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Stroke Type</TableCell>
                            <TableCell>{currentVideo.stroke_type}</TableCell>
                          </TableRow>
                        )}
                        {currentVideo.shot_type && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Shot Type</TableCell>
                            <TableCell>{currentVideo.shot_type}</TableCell>
                          </TableRow>
                        )}
                        {currentVideo.court_position && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Court Position</TableCell>
                            <TableCell>{currentVideo.court_position}</TableCell>
                          </TableRow>
                        )}
                        {currentVideo.half_side && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Half Side</TableCell>
                            <TableCell>{currentVideo.half_side}</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Exam Item</TableCell>
                          <TableCell>{currentVideo.exam_item_name || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                          <TableCell>
                            {currentVideo.uploaded_at
                              ? new Date(currentVideo.uploaded_at).toLocaleString()
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Attempt</TableCell>
                          <TableCell>#{currentVideo.upload_attempt_number || 1}</TableCell>
                        </TableRow>
                        {currentVideo.verification_status && currentVideo.verification_status !== 'pending' && (
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell>
                                <Chip 
                                  label={currentVideo.verification_status === 'approved' ? 'Verified' : currentVideo.verification_status}
                                  size="small"
                                  color={
                                    currentVideo.verification_status === 'approved' ? 'success' :
                                    currentVideo.verification_status === 'rejected' ? 'error' :
                                    'warning'
                                  }
                                />
                              </TableCell>
                            </TableRow>
                            {currentVideo.verifier_name && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Evaluated By</TableCell>
                                <TableCell>{currentVideo.verifier_name}</TableCell>
                              </TableRow>
                            )}
                            {currentVideo.verified_at && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Evaluated At</TableCell>
                                <TableCell>
                                  {new Date(currentVideo.verified_at).toLocaleString()}
                                </TableCell>
                              </TableRow>
                            )}
                            {currentVideo.rating !== null && currentVideo.rating !== undefined && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                                <TableCell>
                                  <Rating value={Number(currentVideo.rating)} readOnly size="small" />
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Evaluation Form */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    Evaluation
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Verification Status</InputLabel>
                    <Select
                      value={verificationStatus}
                      label="Verification Status"
                      onChange={(e) => setVerificationStatus(e.target.value as any)}
                      disabled={currentVideo.verification_status && currentVideo.verification_status !== 'pending'}
                    >
                      <MenuItem value="approved">Verified</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                    {currentVideo.verification_status && currentVideo.verification_status !== 'pending' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        This video has already been evaluated. View-only mode.
                      </Typography>
                    )}
                  </FormControl>

                  <Box sx={{ mb: 2 }}>
                    <Typography component="legend" variant="body2" gutterBottom>
                      Rating (0-5) {verificationStatus === 'rejected' && <Chip label="Not applicable for rejected videos" size="small" color="error" sx={{ ml: 1 }} />}
                    </Typography>
                    <Rating
                      value={rating}
                      onChange={(_, newValue) => setRating(newValue || 0)}
                      max={5}
                      size="large"
                      disabled={verificationStatus === 'rejected' || (currentVideo.verification_status && currentVideo.verification_status !== 'pending')}
                      readOnly={currentVideo.verification_status && currentVideo.verification_status !== 'pending'}
                    />
                    {verificationStatus === 'rejected' && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        Rating is not applicable when video is rejected
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Rating Notes (internal - for admin reference only)"
                    value={ratingNotes}
                    onChange={(e) => setRatingNotes(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={verificationStatus === 'rejected' || (currentVideo.verification_status && currentVideo.verification_status !== 'pending')}
                    helperText="Internal notes about the rating. Not visible to player."
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Admin Feedback (visible to player)"
                    value={adminFeedback}
                    onChange={(e) => setAdminFeedback(e.target.value)}
                    sx={{ mb: 3 }}
                    disabled={currentVideo.verification_status && currentVideo.verification_status !== 'pending'}
                    helperText="Feedback that will be shown to the player. Required for rejected status."
                    required={verificationStatus === 'rejected'}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {currentVideo.verification_status && currentVideo.verification_status !== 'pending' ? (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCurrentVideo(null);
                          setEvaluationStartTime(null);
                          setLockExpiresAt(null);
                        }}
                        fullWidth
                      >
                        Close
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={submitEvaluation}
                          disabled={evaluating}
                          fullWidth
                        >
                          {evaluating ? 'Submitting...' : 'Submit Evaluation'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<SkipNextIcon />}
                          onClick={releaseVideo}
                          disabled={evaluating}
                        >
                          Skip
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default AdminVideoEvaluationQueue;

